import { generateClient } from 'aws-amplify/data'
import { type Schema } from '../../amplify/data/resource'
import { fetchAuthSession } from 'aws-amplify/auth'
//import { del } from 'aws-amplify/api'

const client = generateClient<Schema>()

export interface Device {
  id: string
  deviceId: string
  userId: string
  deviceName: string
  registrationCode?: string
  status?: 'online' | 'offline' | 'error'
  lastSeen?: string
  ipAddress?: string
  macAddress?: string
  version?: string
  location?: string
  iotThingName?: string
  iotCertificateId?: string
  createdAt?: string
  updatedAt?: string
}

export interface DeviceStats {
  totalDevices: number
  onlineDevices: number
  offlineDevices: number
  errorDevices: number
}

class DeviceService {
  /**
   * Get all devices for the current user
   */
  async getDevices(): Promise<Device[]> {
    try {
      const session = await fetchAuthSession()
      const userId = session.userSub

      if (!userId) {
        throw new Error('User not authenticated')
      }

      const { data: devices, errors } = await client.models.Device.listDeviceByUserIdAndCreatedAt({
        userId,
      })

      if (errors) {
        console.error('Error fetching devices:', errors)
        throw new Error('Failed to fetch devices')
      }

      return devices.map(device => ({
        id: device.id,
        deviceId: device.deviceId,
        userId: device.userId,
        deviceName: device.deviceName,
        registrationCode: device.registrationCode || undefined,
        status: device.status || 'offline',
        lastSeen: device.lastSeen || undefined,
        ipAddress: device.ipAddress || undefined,
        macAddress: device.macAddress || undefined,
        version: device.version || undefined,
        location: device.locationId || undefined,
        iotThingName: device.iotThingName || undefined,
        iotCertificateId: device.iotCertificateId || undefined,
        createdAt: device.createdAt || undefined,
        updatedAt: device.updatedAt || undefined,
      }))
    } catch (error) {
      console.error('Error in getDevices:', error)
      throw error
    }
  }

  /**
   * Get device statistics
   */
  async getDeviceStats(): Promise<DeviceStats> {
    try {
      const devices = await this.getDevices()
      
      const stats = devices.reduce(
        (acc, device) => {
          acc.totalDevices++
          switch (device.status) {
            case 'online':
              acc.onlineDevices++
              break
            case 'offline':
              acc.offlineDevices++
              break
            case 'error':
              acc.errorDevices++
              break
            default:
              acc.offlineDevices++
          }
          return acc
        },
        { totalDevices: 0, onlineDevices: 0, offlineDevices: 0, errorDevices: 0 }
      )

      return stats
    } catch (error) {
      console.error('Error in getDeviceStats:', error)
      throw error
    }
  }

  /**
   * Get a specific device by ID
   */
  async getDevice(deviceId: string): Promise<Device | null> {
    try {
      const { data: devices, errors } = await client.models.Device.listDeviceByDeviceId({
        deviceId,
      })

      if (errors) {
        console.error('Error fetching device:', errors)
        throw new Error('Failed to fetch device')
      }

      if (devices.length === 0) {
        return null
      }

      const device = devices[0]
      return {
        id: device.id,
        deviceId: device.deviceId,
        userId: device.userId,
        deviceName: device.deviceName,
        registrationCode: device.registrationCode || undefined,
        status: device.status || 'offline',
        lastSeen: device.lastSeen || undefined,
        ipAddress: device.ipAddress || undefined,
        macAddress: device.macAddress || undefined,
        version: device.version || undefined,
        location: device.locationId || undefined,
        iotThingName: device.iotThingName || undefined,
        iotCertificateId: device.iotCertificateId || undefined,
        createdAt: device.createdAt || undefined,
        updatedAt: device.updatedAt || undefined,
      }
    } catch (error) {
      console.error('Error in getDevice:', error)
      throw error
    }
  }

  /**
   * Update device information
   */
  async updateDevice(deviceId: string, updates: Partial<Device>): Promise<Device> {
    try {
      const device = await this.getDevice(deviceId)
      if (!device) {
        throw new Error('Device not found')
      }

      const { data: updatedDevice, errors } = await client.models.Device.update({
        id: device.id,
        ...updates,
      })

      if (errors) {
        console.error('Error updating device:', errors)
        throw new Error('Failed to update device')
      }

      return {
        id: updatedDevice?.id ?? '',
        deviceId: updatedDevice?.deviceId ?? '',
        userId: updatedDevice?.userId ?? '',
        deviceName: updatedDevice?.deviceName ?? '',
        registrationCode: updatedDevice?.registrationCode ?? undefined,
        status: updatedDevice?.status ?? 'offline',
        lastSeen: updatedDevice?.lastSeen ?? undefined,
        ipAddress: updatedDevice?.ipAddress ?? undefined,
        macAddress: updatedDevice?.macAddress ?? undefined,
        version: updatedDevice?.version ?? undefined,
        location: updatedDevice?.locationId ?? undefined,
        iotThingName: updatedDevice?.iotThingName ?? undefined,
        iotCertificateId: updatedDevice?.iotCertificateId ?? undefined,
        createdAt: updatedDevice?.createdAt ?? undefined,
        updatedAt: updatedDevice?.updatedAt ?? undefined,
      }
    } catch (error) {
      console.error('Error in updateDevice:', error)
      throw error
    }
  }

  /**
   * Delete a device with complete IoT cleanup and release its registration code for reuse
   */
  async deleteDevice(deviceId: string): Promise<void> {
    try {
      const device = await this.getDevice(deviceId)
      if (!device) {
        throw new Error('Device not found')
      }

      // First, release the registration code if it exists
      if (device.registrationCode) {
        await this.releaseRegistrationCode(device.registrationCode)
      }

      // Use the custom GraphQL mutation for complete device deletion with IoT cleanup
      const result = await client.graphql({
        query: `
          mutation DeleteDeviceComplete($deviceId: String!) {
            deleteDeviceComplete(deviceId: $deviceId)
          }
        `,
        variables: {
          deviceId: deviceId
        }
      }) as { data?: unknown; errors?: Array<{ message?: string }> }

      if (result.errors) {
        console.error('Error deleting device with IoT cleanup:', result.errors)
        throw new Error('Failed to delete device: ' + result.errors[0]?.message)
      }

      console.log('Device deleted successfully with IoT cleanup:', result.data)
    } catch (error) {
      console.error('Error in deleteDevice:', error)
      throw error
    }
  }

  /**
   * Release a registration code for reuse
   */
  private async releaseRegistrationCode(registrationCode: string): Promise<void> {
    try {
      // Find the registration code by its code value
      const { data: codes, errors: queryErrors } = await client.models.RegistrationCode.listRegistrationCodeByCode({
        code: registrationCode,
      })

      if (queryErrors) {
        console.error('Error finding registration code:', queryErrors)
        return // Don't fail device deletion if we can't release the code
      }

      if (codes.length > 0) {
        const codeRecord = codes[0]
        
        // Mark the code as unused and clear device-specific info
        const { errors: updateErrors } = await client.models.RegistrationCode.update({
          id: codeRecord.id,
          isUsed: false,
          deviceName: null, // Clear the device name if it was set
        })

        if (updateErrors) {
          console.error('Error releasing registration code:', updateErrors)
          // Don't fail device deletion if we can't release the code
        } else {
          console.log(`Registration code ${registrationCode} has been released for reuse`)
        }
      }
    } catch (error) {
      console.error('Error in releaseRegistrationCode:', error)
      // Don't fail device deletion if we can't release the code
    }
  }

  /**
   * Send command to device via IoT
   */
  async sendDeviceCommand(deviceId: string, command: unknown): Promise<void> {
    try {
      // This would typically call a Lambda function that sends IoT commands
      const session = await fetchAuthSession()
      const token = session.tokens?.accessToken?.toString()

      if (!token) {
        throw new Error('No access token available')
      }

      const response = await fetch('/api/device-command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          deviceId,
          command,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send device command')
      }
    } catch (error) {
      console.error('Error in sendDeviceCommand:', error)
      throw error
    }
  }
}

export const deviceService = new DeviceService()
export default deviceService